import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ragService from '../../services/rag.service';
import { dmsAPI } from '../../services/api';

const DMSSync = () => {
  const [collectionId, setCollectionId] = useState('');
  const [collections, setCollections] = useState([]);
  const [dmsConfig, setDmsConfig] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedDMS, setSelectedDMS] = useState('');
  const [folderPath, setFolderPath] = useState('');
  const [syncOptions, setSyncOptions] = useState({
    include_subfolders: true,
    file_types: ['pdf', 'docx', 'doc', 'txt', 'md', 'xlsx', 'pptx'],
    max_file_size_mb: 50
  });
  const [syncProgress, setSyncProgress] = useState(null);
  const navigate = useNavigate();

  const dmsTypes = [
    { id: 'sharepoint', name: 'SharePoint', icon: '📁', color: 'blue' },
    { id: 'onedrive', name: 'OneDrive', icon: '☁️', color: 'sky' },
    { id: 'google_drive', name: 'Google Drive', icon: '📂', color: 'yellow' },
    { id: 'dropbox', name: 'Dropbox', icon: '📦', color: 'blue' },
    { id: 'box', name: 'Box', icon: '📋', color: 'blue' },
    { id: 'confluence', name: 'Confluence', icon: '📝', color: 'blue' },
    { id: 's3', name: 'AWS S3', icon: '🪣', color: 'orange' },
    { id: 'azure_blob', name: 'Azure Blob', icon: '☁️', color: 'blue' },
    { id: 'alfresco', name: 'Alfresco', icon: '📄', color: 'green' },
    { id: 'documentum', name: 'Documentum', icon: '📑', color: 'purple' }
  ];

  const fileTypeOptions = [
    { type: 'pdf', label: 'PDF', icon: '📕' },
    { type: 'docx', label: 'Word (DOCX)', icon: '📘' },
    { type: 'doc', label: 'Word (DOC)', icon: '📘' },
    { type: 'txt', label: 'Text', icon: '📄' },
    { type: 'md', label: 'Markdown', icon: '📝' },
    { type: 'xlsx', label: 'Excel', icon: '📗' },
    { type: 'pptx', label: 'PowerPoint', icon: '📙' },
    { type: 'html', label: 'HTML', icon: '🌐' },
    { type: 'csv', label: 'CSV', icon: '📊' }
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [collectionsRes, dmsConfigRes] = await Promise.all([
        ragService.getCollections(),
        dmsAPI.getConfig()
      ]);
      
      setCollections(collectionsRes.collections || []);
      setDmsConfig(dmsConfigRes.data?.config || {});
      
      // Do NOT auto-select collection — user must explicitly choose
      
      // Set default DMS to active one
      const activeDMS = dmsConfigRes.data?.config?.active_dms;
      if (activeDMS) {
        setSelectedDMS(activeDMS);
      }
    } catch (err) {
      console.error('Failed to load data', err);
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async (e) => {
    e.preventDefault();

    if (!collectionId) {
      setError('Please select a collection');
      return;
    }

    if (!selectedDMS) {
      setError('Please select a DMS to sync from');
      return;
    }

    const dmsTypeConfig = dmsConfig?.[selectedDMS];
    if (!dmsTypeConfig?.enabled) {
      setError(`${getDMSDisplayName(selectedDMS)} is not enabled. Please configure it first.`);
      return;
    }

    setSyncing(true);
    setError('');
    setSuccessMessage('');
    setSyncProgress({ status: 'Connecting to DMS...', progress: 10 });

    try {
      setSyncProgress({ status: 'Fetching documents...', progress: 30 });
      
      // Call backend to sync DMS documents to RAG - collection_id is now UUID string
      const response = await ragService.syncDMSData({
        collection_id: collectionId,
        dms_type: selectedDMS,
        folder_path: folderPath || null,
        include_subfolders: syncOptions.include_subfolders,
        file_types: syncOptions.file_types,
        max_file_size_mb: syncOptions.max_file_size_mb
      });

      setSyncProgress({ status: 'Completed', progress: 100 });
      setSuccessMessage(
        `Successfully synced ${response.documents_created || 0} documents from ${getDMSDisplayName(selectedDMS)}. ` +
        `${response.chunks_created || 0} chunks created.`
      );
      
    } catch (err) {
      console.error('Sync failed', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Sync failed';
      setError(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    } finally {
      setSyncing(false);
      setSyncProgress(null);
    }
  };

  const getDMSDisplayName = (dmsType) => {
    const dms = dmsTypes.find(d => d.id === dmsType);
    return dms?.name || dmsType;
  };

  const getEnabledDMSList = () => {
    return dmsTypes.filter(dms => dmsConfig?.[dms.id]?.enabled);
  };

  const toggleFileType = (type) => {
    setSyncOptions(prev => ({
      ...prev,
      file_types: prev.file_types.includes(type)
        ? prev.file_types.filter(t => t !== type)
        : [...prev.file_types, type]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (collections.length === 0) {
    return (
      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">DMS to RAG Sync</h1>
          <p className="mt-1 text-sm text-gray-600">Sync documents from your DMS to the vector database</p>
        </div>

        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-8 text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-yellow-900 mb-2">No Collections Found</h2>
          <p className="text-yellow-800 mb-6">
            You need to create a collection before syncing DMS documents.
          </p>
          <button
            onClick={() => navigate('/rag/collections')}
            className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Create Collection
          </button>
        </div>
      </div>
    );
  }

  const enabledDMSList = getEnabledDMSList();

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">DMS to RAG Sync</h1>
        <p className="mt-1 text-sm text-gray-600">
          Sync documents from SharePoint, Google Drive, S3, and other document management systems
        </p>
      </div>

      {/* DMS Status Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
        {dmsTypes.slice(0, 5).map((dms) => {
          const isEnabled = dmsConfig?.[dms.id]?.enabled;
          const isSelected = selectedDMS === dms.id;
          return (
            <button
              key={dms.id}
              onClick={() => isEnabled && setSelectedDMS(dms.id)}
              disabled={!isEnabled}
              className={`p-4 rounded-lg border-2 transition-all text-left ${
                isSelected
                  ? 'border-indigo-500 bg-indigo-50'
                  : isEnabled
                    ? 'border-gray-200 hover:border-gray-300 bg-white'
                    : 'border-gray-100 bg-gray-50 cursor-not-allowed opacity-60'
              }`}
            >
              <div className="text-2xl mb-2">{dms.icon}</div>
              <div className="font-medium text-gray-900 text-sm">{dms.name}</div>
              <div className={`text-xs mt-1 ${isEnabled ? 'text-green-600' : 'text-gray-400'}`}>
                {isEnabled ? '● Connected' : '○ Not configured'}
              </div>
            </button>
          );
        })}
      </div>

      {enabledDMSList.length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <p className="text-yellow-800 mb-4">No DMS systems are configured and enabled.</p>
          <button
            onClick={() => navigate('/dms-config')}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            Configure DMS
          </button>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
          <span className="block sm:inline">{error}</span>
          <button onClick={() => setError('')} className="absolute top-0 bottom-0 right-0 px-4 py-3">
            <span className="text-2xl">&times;</span>
          </button>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative">
          <span className="block sm:inline">{successMessage}</span>
          <button onClick={() => setSuccessMessage('')} className="absolute top-0 bottom-0 right-0 px-4 py-3">
            <span className="text-2xl">&times;</span>
          </button>
        </div>
      )}

      {/* Sync Form */}
      {selectedDMS && dmsConfig?.[selectedDMS]?.enabled && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          <div className="flex items-center space-x-3 mb-6">
            <span className="text-3xl">{dmsTypes.find(d => d.id === selectedDMS)?.icon}</span>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Sync from {getDMSDisplayName(selectedDMS)}
              </h2>
              <p className="text-sm text-gray-500">Configure sync options and start importing documents</p>
            </div>
          </div>

          <form onSubmit={handleSync} className="space-y-6">
            {/* Target Collection — explicit selection required */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                🎯 Target Collection <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Select the collection where synced documents will be embedded and stored.
              </p>

              {!collectionId && (
                <div className="mb-3 p-3 bg-amber-50 border border-amber-300 rounded-lg flex items-start gap-2">
                  <span className="text-amber-500 text-lg">⚠️</span>
                  <p className="text-sm text-amber-700 font-medium">
                    You must select a target collection before syncing.
                  </p>
                </div>
              )}

              <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                {collections.length === 0 ? (
                  <p className="p-4 text-sm text-gray-400 text-center">No collections available</p>
                ) : (
                  collections.map((collection) => {
                    const optionId = String(collection.id);
                    const optionName = collection.name || collection.collection_name;
                    const isSelected = collectionId === optionId;
                    return (
                      <label
                        key={collection.id}
                        className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
                          isSelected
                            ? 'bg-indigo-50 border-l-4 border-indigo-500'
                            : 'hover:bg-gray-50 border-l-4 border-transparent'
                        }`}
                      >
                        <input
                          type="radio"
                          name="targetCollection"
                          value={optionId}
                          checked={isSelected}
                          onChange={() => setCollectionId(optionId)}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500"
                        />
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium truncate ${isSelected ? 'text-indigo-700' : 'text-gray-700'}`}>
                            {optionName}
                          </p>
                          <p className="text-xs text-gray-400">{collection.documents_count || 0} docs</p>
                        </div>
                        {isSelected && <span className="text-indigo-500 text-lg">✅</span>}
                      </label>
                    );
                  })
                )}
              </div>
            </div>

            {/* Folder Path (optional) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Folder Path (Optional)
              </label>
              <input
                type="text"
                value={folderPath}
                onChange={(e) => setFolderPath(e.target.value)}
                placeholder={selectedDMS === 's3' ? 'prefix/folder/' : '/Documents/MyFolder'}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              <p className="mt-1 text-xs text-gray-500">
                Leave empty to sync all documents from the configured root
              </p>
            </div>

            {/* File Types Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                File Types to Sync
              </label>
              <div className="flex flex-wrap gap-2">
                {fileTypeOptions.map(({ type, label, icon }) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => toggleFileType(type)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center space-x-1 ${
                      syncOptions.file_types.includes(type)
                        ? 'bg-indigo-100 text-indigo-700 border-2 border-indigo-300'
                        : 'bg-gray-100 text-gray-600 border-2 border-transparent hover:bg-gray-200'
                    }`}
                  >
                    <span>{icon}</span>
                    <span>{label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Additional Options */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="include_subfolders"
                  checked={syncOptions.include_subfolders}
                  onChange={(e) => setSyncOptions(prev => ({ ...prev, include_subfolders: e.target.checked }))}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="include_subfolders" className="text-sm font-medium text-gray-700">
                  Include subfolders
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max file size (MB)
                </label>
                <input
                  type="number"
                  value={syncOptions.max_file_size_mb}
                  onChange={(e) => setSyncOptions(prev => ({ ...prev, max_file_size_mb: parseInt(e.target.value) || 50 }))}
                  min="1"
                  max="500"
                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            {/* Sync Progress */}
            {syncProgress && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-blue-800">{syncProgress.status}</span>
                  <span className="text-sm text-blue-600">{syncProgress.progress}%</span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${syncProgress.progress}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* Sync Button */}
            <button
              type="submit"
              disabled={syncing || !collectionId || !selectedDMS}
              className="w-full px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center space-x-2"
            >
              {syncing ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Syncing Documents...</span>
                </>
              ) : (
                <>
                  <span>📥</span>
                  <span>Sync Documents to RAG</span>
                </>
              )}
            </button>
          </form>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h3 className="text-sm font-semibold text-blue-900 mb-2">📝 DMS Sync Instructions</h3>
        <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
          <li>Configure your DMS connections in the DMS Integration settings first</li>
          <li>Select a DMS system that is connected and enabled</li>
          <li>Choose a target collection for the synced documents</li>
          <li>Optionally specify a folder path to limit the sync scope</li>
          <li>Select which file types to include in the sync</li>
          <li>Documents will be downloaded, processed, and indexed for semantic search</li>
        </ul>
      </div>
    </div>
  );
};

export default DMSSync;
